import React, { useEffect } from 'react';
import { Box, Container, Typography, Card, CardContent, Button, Stack, IconButton, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { motion } from 'framer-motion';
import ChatbotWidget from '../components/ChatbotWidget';

const videos = [
  { title: 'Getting Started: Connect Your Accounts', ytId: 'A6yCjYV7jtE' },
  { title: 'Create Custom Prompts (with AI help)', ytId: '0OCgPAGd03Y' },
  { title: 'Prompt Tester: Simulate Real Responses', ytId: 'QVexBD968zU' }
];

const PublicHelpCenter: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTryNow = () => {
    navigate('/login');
  };

  return (<>
    <Box sx={{ minHeight: '100vh', overflow: 'hidden' }}>
      {/* Header - Same as Landing */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            py: 2,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {/* Logo */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer' }}
                  onClick={() => { navigate('/'); scrollToTop(); }}
                >
                  <Logo height={48} />
                </Box>
              </motion.div>

              {/* Navigation */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Stack 
                  direction={{ xs: 'column', md: 'row' }} 
                  spacing={{ xs: 1, md: 3 }} 
                  alignItems="center"
                  sx={{ display: { xs: 'none', md: 'flex' } }}
                >
                  <Button
                    variant="text"
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Features
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Pricing
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/')}
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Support
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleTryNow}
                    sx={{
                      borderColor: 'rgba(16, 185, 129, 0.3)',
                      color: '#10B981',
                      fontWeight: 600,
                      px: 3,
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      '&:hover': {
                        borderColor: '#10B981',
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Stack>
                
                {/* Mobile Menu Button */}
                <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Button
                    variant="outlined"
                    onClick={handleTryNow}
                    size="small"
                    sx={{
                      borderColor: 'rgba(16, 185, 129, 0.3)',
                      color: '#10B981',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#10B981',
                        background: 'rgba(16, 185, 129, 0.05)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                </Box>
              </motion.div>
            </Box>
          </Container>
        </Box>
      </motion.div>

      {/* Animated Background */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(0deg, #f1fff9, #f1fff900 30%), radial-gradient(circle at 0 0, #5fd5b6 0, #5fd5b600 30%), linear-gradient(180deg, #79dfffb3 -10%, #79dfff00 70%), radial-gradient(circle at 95% 0, #d0f224 0, #5fd5b600 20%)',
          zIndex: -1,
        }}
      />

      {/* Main Content */}
      <Box sx={{ pt: 16, pb: 8, minHeight: '70vh' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, background: 'linear-gradient(90deg,#0ea5e9,#22c55e)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Help Center
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Short, practical videos to help you set up and get the most out of Social In One.
          </Typography>

          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            {videos.map((v) => (
              <Box key={v.ytId}>
                <Card elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <Box sx={{ position: 'relative', pt: '56.25%', backgroundColor: '#000' }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${v.ytId}`}
                      title={v.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{v.title}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Contact Callout */}
          <Box
            sx={{
              mt: 5,
              p: 3,
              borderRadius: 3,
              textAlign: 'center',
              border: '1px solid rgba(0,0,0,0.06)',
              background: 'linear-gradient(135deg, rgba(14,165,233,0.06), rgba(34,197,94,0.06))'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Still need help?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If the videos or chatbot didn't solve your issue, don't hesitate to contact us.
            </Typography>
            <Button
              component="a"
              href="mailto:info@socialinone.ai"
              variant="contained"
              sx={{
                textTransform: 'none',
                px: 2.5,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                background: 'linear-gradient(90deg, #0ea5e9, #22c55e)',
                boxShadow: '0 8px 20px rgba(34,197,94,0.25)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #0284c7, #16a34a)'
                }
              }}
            >
              info@socialinone.ai
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer - Same as Landing */}
      <Box
        id="footer"
        component="footer"
        sx={{
          background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)',
          color: 'white',
          py: 8,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
              gap: 4,
            }}
          >
            {/* Company Info */}
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Logo height={40} />
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.8,
                  maxWidth: { xs: '100%', md: '64%' },
                  mx: { xs: 'auto', md: 0 }
                }}
              >
                Transform your social media presence with AI-powered automation. 
                Engage with your audience 24/7 and grow your brand effortlessly.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <IconButton
                  component="a"
                  href="https://www.facebook.com/socialinone"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { color: '#1877F2' },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.instagram.com/socialinone/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { color: '#E1306C' },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.tiktok.com/@socialinone"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { color: '#00f2ea' },
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    sx={{ width: 24, height: 24, fill: 'currentColor' }}
                  >
                    <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25v178.72A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0h88a121.18 121.18 0 0 0 1.86 22.17A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z"/>
                  </Box>
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.youtube.com/@SocialinOne"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { color: '#FF0000' },
                  }}
                >
                  <YouTubeIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* Support */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Support
              </Typography>
              <Stack spacing={1} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/faq')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  FAQ
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/contact')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Contact Us
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/help-center')}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    textTransform: 'none',
                    '&:hover': { color: '#10B981' },
                  }}
                >
                  Help Center
                </Button>
              </Stack>
            </Box>

            {/* Contact Info */}
            <Box sx={{ ml: { xs: 0, md: 'auto' }, textAlign: { xs: 'center', md: 'left' } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: 'white',
                }}
              >
                Contact
              </Typography>
              <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <EmailIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    info@socialinone.ai
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <LocationOnIcon sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}
                  >
                    Almere, Netherlands
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Bottom Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'center', md: 'center' },
              gap: 2,
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
              }}
            >
              © 2025 NowUp. All rights reserved.
            </Typography>
            <Stack
              direction="row"
              spacing={3}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 3 },
                ml: { xs: 0, md: 0 },
              }}
            >
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/privacy-policy')}
              >
                Privacy Policy
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/terms-of-use')}
              >
                Terms of Service
              </Button>
              <Button
                variant="text"
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'none',
                  marginLeft: { xs: '0 !important'},
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  '&:hover': { color: '#10B981' },
                }}
                onClick={() => navigate('/cookie-policy')}
              >
                Cookie Policy
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
    <ChatbotWidget />
  </>);
};

export default PublicHelpCenter;
